/**
 * CDA two-channel color model.
 *
 * Governance is NOT severity. A delete tool with per-tool consent + step-up MFA
 * is well-controlled, not dangerous. We use two independent visual channels so a
 * heavily-gated destructive tool reads as *controlled*, never alarming:
 *
 *   - RISK channel       — inherent risk / impact. Red is legitimate here
 *                          (delete, discloses-value, high/critical risk).
 *   - COVERAGE channel    — governance coverage. Reads as reassurance
 *                          (blue/green): confirmation, step-up, bounded consent,
 *                          signed receipts.
 *
 * The scariest state is risk-present + coverage-absent (a mutating tool with no
 * contract) — surfaced as the governance gap, in the warning/alert color.
 *
 * All values are CSS variables (with literal fallbacks) — zero hardcoded-only
 * colors, theme-driven, WCAG-aware.
 */

import type { CdaRiskLevel, GovernanceState } from './types';
import type { GateChannel } from './planGates';

// --- RISK channel (inherent danger) -----------------------------------------
export const RISK_COLOR: Record<CdaRiskLevel, string> = {
  read: 'var(--color-success, #22c55e)',
  low: 'var(--color-success, #22c55e)',
  medium: 'var(--color-warning, #f59e0b)',
  high: 'var(--color-danger, #ef4444)',
  critical: 'var(--color-danger, #dc2626)',
};

export const RISK_LABEL: Record<CdaRiskLevel, string> = {
  read: 'Read only',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

// --- Theme-safe typography (shell tokens first; WCAG fallbacks for light surfaces) ---
/** Primary body/heading text — follows Experience ``--main-text``. */
export const TEXT_PRIMARY = 'var(--main-text, var(--color-text-primary, #0f172a))';
/** Secondary / supporting text — AA on light and dark surfaces. */
export const TEXT_SECONDARY = 'var(--main-text-secondary, var(--color-text-secondary, #475569))';
/** Captions, hints, tertiary labels. */
export const TEXT_TERTIARY = 'var(--main-text-tertiary, var(--color-text-muted, #64748b))';
/** Opaque inset surface for nested panels (modals, sections). */
export const SURFACE_INSET = 'var(--card-bg-solid, var(--surface-inset, var(--main-bg-elevated, #f8fafc)))';
/** Standard panel border. */
export const SURFACE_BORDER = 'var(--border-color, rgba(15, 23, 42, 0.12))';

// --- COVERAGE channel (reassurance) ------------------------------------------
/** Strong coverage — green reassurance. */
export const COVERAGE_STRONG = 'var(--color-success, #22c55e)';
/** Present coverage — calm blue. */
export const COVERAGE_PRESENT = 'var(--color-info, #3b82f6)';
/** Neutral / mechanical. */
export const NEUTRAL = TEXT_SECONDARY;
/** Gap / finding — warning. */
export const GAP_COLOR = 'var(--color-warning, #f59e0b)';

/** Primary color for the single governance signal, by state. */
export function governanceStateColor(state: GovernanceState): string {
  switch (state) {
    case 'read-only':
      return NEUTRAL;
    case 'gap':
      return GAP_COLOR;
    case 'declared':
      return COVERAGE_PRESENT;
    case 'governed':
      return COVERAGE_PRESENT;
    case 'governed-strong':
      return COVERAGE_STRONG;
    case 'unknown':
    default:
      return NEUTRAL;
  }
}

/** Color for a gate step, by channel + state. */
export function gateChannelColor(channel: GateChannel): string {
  switch (channel) {
    case 'risk':
      return 'var(--color-danger, #ef4444)';
    case 'coverage':
      return COVERAGE_PRESENT;
    case 'neutral':
    default:
      return NEUTRAL;
  }
}

/** A translucent fill derived from a solid color (opaque-safe via color-mix). */
export function softFill(color: string, pct = 12): string {
  return `color-mix(in srgb, ${color} ${pct}%, transparent)`;
}

export function softBorder(color: string, pct = 28): string {
  return `color-mix(in srgb, ${color} ${pct}%, transparent)`;
}
